// utils/axiosApp.js
import axios from "axios"
import { BASE_API_URL, BASE_AUTH_URL, BASE_LOGIN_URL } from "../config"

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

const showSessionExpiredMessage = () => {
  const notification = document.createElement("div")
  notification.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #ff6b6b, #ee5a24);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 12px;
      animation: slideIn 0.3s ease-out;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255,255,255,0.2);
    ">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 
        10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 
        1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
      <div>
        <div style="font-weight: 600; margin-bottom: 2px;">Session Expired</div>
        <div style="opacity: 0.9; font-size: 12px;">Redirecting you to login page...</div>
      </div>
    </div>
  `
  const style = document.createElement("style")
  style.textContent = `
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `
  document.head.appendChild(style)
  document.body.appendChild(notification)
  setTimeout(() => {
    if (notification.parentNode) {
      notification.style.animation = "slideIn 0.3s ease-out reverse"
      setTimeout(() => notification.remove(), 300)
    }
  }, 1700)
}

const redirectToLogin = () => {
  showSessionExpiredMessage()
  const currentUrl = window.location.href
  const encodedRedirect = encodeURIComponent(currentUrl)
  setTimeout(() => {
    window.location.href = `${BASE_LOGIN_URL}?redirectTo=${encodedRedirect}`
  }, 2000)
}

export const axiosApp = axios.create({
  baseURL: BASE_API_URL,
  withCredentials: true,
})

// 🔄 Response interceptor: Handle token expiration
axiosApp.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If refresh is already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(() => {
          return axiosApp(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        console.log("🔄 Token expired, attempting refresh...")
        await axios.get(`${BASE_AUTH_URL}/auth/refresh`, { withCredentials: true })
        
        console.log("✅ Token refreshed successfully")
        processQueue(null, true)
        isRefreshing = false
        
        // Retry the original request
        return axiosApp(originalRequest)
        
      } catch (refreshError) {
        console.error("⛔ Token refresh failed:", refreshError)
        processQueue(refreshError, null)
        isRefreshing = false
        
        // Redirect to login if refresh fails
        redirectToLogin()
        return Promise.reject(refreshError)
      }
    }

    console.error("❌ API Error:", error)
    return Promise.reject(error)
  }
)